import i18n from "src/domains/shared/constants/locale/i18n";

/**
 * 비밀번호 유효성 검증
 * - 8자 이상, 대문자/소문자/숫자/특수문자 포함 여부를 개별 체크합니다.
 * - 조건을 만족하지 못한 항목의 경고 문구 리스트를 반환합니다.
 *
 * NOTE: utility 함수라 React hook(useTranslation) 호출 불가. i18n 인스턴스 .t 를
 * 직접 사용한다. 호출 시점의 i18n 전역 현재 언어로 메시지를 반환한다.
 */
export const validationPassword = (password: string) => {
  const t = i18n.t.bind(i18n);
  const warnList: string[] = [];
  const regaxForValSignUp = {
    moreThanEightLength: {
      warnText: t("myPage.msg.passwordLength", { ns: "common" }),
      fn: new RegExp("(?=.{8,})"),
    },
    atLeastOneUpperCaseLetter: {
      warnText: t("myPage.msg.passwordNeedCapital", { ns: "common" }),
      fn: new RegExp("(?=.*[A-Z])"),
    },
    atLeastOneLowerCaseLetter: {
      warnText: t("myPage.msg.atLeastOneLowerCaseLetter", { ns: "common" }),
      fn: new RegExp("(?=.*[a-z])"),
    },
    atLeastOneDigit: {
      warnText: t("myPage.msg.atLeastOneDigit", { ns: "common" }),
      fn: new RegExp("(?=.*[0-9])"),
    },
    atLeastOneSpecialCharacter: {
      warnText: t("myPage.msg.atLeastOneSpecialCharacter", { ns: "common" }),
      fn: new RegExp("([^A-Za-z0-9])"),
    },
  };
  for (const [key, value] of Object.entries(regaxForValSignUp)) {
    if (!value.fn.test(password)) {
      warnList.push(value.warnText);
    }
  }
  return warnList;
};

/**
 * 휴대폰 번호 유효성 검증
 * - 길이(10~11자리) 및 숫자만 포함하는지 검증합니다.
 * - 조건을 만족하지 못한 항목의 경고 문구 리스트를 반환합니다.
 *
 * NOTE: 메시지는 호출 시점의 i18n 전역 현재 언어로 반환한다.
 */
export const validationPhoneNumber = (phoneNumber: string) => {
  const t = i18n.t.bind(i18n);
  const warnList: string[] = [];

  // 자리수 검증 (10, 11자리)
  if (phoneNumber.length < 10 || phoneNumber.length > 11) {
    warnList.push(t("myPage.msg.notEnoughLength", { ns: "common" }));
  }
  if (!Number(phoneNumber)) {
    // 숫자만 들어오는지
    warnList.push(t("myPage.msg.onlyNumber", { ns: "common" }));
  }

  return warnList;
};

/**
 * 국가번호 +82 형식으로 변환
 * - 입력 예: 01012345678 -> 출력: +821012345678
 */
export const convertToNumberWithNationCode = (phoneNumber: string) => {
  const removedFirstNumber = phoneNumber.slice(1, phoneNumber.length).concat();
  return `+82${removedFirstNumber}`;
};

/**
 * 국가번호(+82)를 제거하고 0으로 시작하는 국내 번호로 변환
 * - 입력 예: +821012345678 -> 출력: 01012345678
 */
export const convertToNumberWithoutNationCode = (phoneNumber: string) => {
  const removedNationCode = phoneNumber.slice(3, phoneNumber.length);
  return `0${removedNationCode}`;
};

/**
 * 숫자 혹은 소수점 하나를 포함한 숫자 형식인지 검증
 * - 정규식: ^\d*\.?\d*$ (선택적 소수점 1개 허용)
 */
export const validateNumber = (value: string) => {
  const numRegExp = /^\d*\.?\d*$/;
  return numRegExp.test(value);
};

/**
 * 점 간격 첫 자리 유효성 검증 (그래프 입력용)
 * - 1~2자리 정수 또는 소수 첫째 자리까지 허용, 0 단독은 불가
 * - 정규식: /^(?!0$)\d{1,2}(\.\d)?$/
 */
export const validateFirstDigit = (value: string) => {
  const firstDigitRegExp = /^(?!0$)\d{1,2}(\.\d)?$/;

  return firstDigitRegExp.test(value);
};
